import PropTypes from 'prop-types';
import {
  Button,
  Dropdown,
  Pagination,
  Table,
  TableProps,
} from 'semantic-ui-react';
import React, { useEffect, useReducer, useState } from 'react';
import useBreakpoint, { SIZE_LG, SIZE_XL, SIZE_XXL } from './useBreakpoint';
import useTransition from './useTransition';
import _ from 'lodash';
import classNames from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import './style/BaseList.less';
import sortSVG from './icons/sort.svg';
// @ts-ignore
import { Icon } from '@plone/volto/components';

export const messages = defineMessages({
  trans_list_empty_message: {
    id: 'trans_list_empty_message',
    defaultMessage: 'No entries found.',
  },
  trans_size_options: {
    id: 'trans_list_size_options',
    defaultMessage: 'Listsize options',
  },
  trans_show_more_entries: {
    id: 'trans_show_more_entries',
    defaultMessage: 'More results',
  },
  trans_show_all: {
    id: 'trans_show_all',
    defaultMessage: 'All',
  },
});

type TListEntryValue = string | { (): string | number } | number | null;
export type TListEntry = Record<string, TListEntryValue>;
export type TSortDir = 'asc' | 'desc';
type TLabel = JSX.Element | string | (() => JSX.Element | string | null);
export interface TableColumn<Item> {
  label: TLabel;
  preLabel?: TLabel;
  postLabel?: TLabel;
  cellComponent?: null | ((entry: Item) => JSX.Element | string | null);
  sortable?: boolean;
  className?: string;
  sortCmp?: { (a: Item, b: Item): number };
}

export interface ListProps<Item extends TListEntry> extends TableProps {
  itemColumns: Record<keyof Item, TableColumn<Item>>;
  items: Item[];
  // needs to be a key of columns
  sortColumn?: keyof Item;
  // needs to be a key of columns
  defaultSortColumn?: keyof Item;
  sortDir?: TSortDir;
  // Function gets called with entry as param on click
  onEntryClick?: { (entry: Item): void };
  onSort?: { (sortColumn: keyof Item, sortDir: TSortDir): void };
  emptyMessage?: string;
  collapsed?: boolean;
  pagination?: boolean;
  batched?: boolean | number;
  sizeOptions?: number[];
}

export const BaseList = <Item extends TListEntry>({
  itemColumns,
  items,
  sortColumn: parentSortColumn,
  defaultSortColumn,
  sortDir: parentSortDir,
  onEntryClick,
  onSort,
  emptyMessage,
  pagination,
  batched,
  collapsed,
  sizeOptions: parentSizeOptions,
  ...extra_props
}: ListProps<Item>): JSX.Element => {
  const [paginationSiblings, setPaginationSiblings] = useState(0);
  const [sizeOptions, setSizeOptions] = useState(
    (parentSizeOptions ?? [10, 20, 50, 100]).map((size) => {
      return {
        key: size,
        text: size,
        value: size,
      };
    }),
  );
  const size = useBreakpoint();
  const intl = useIntl();
  const batchSize = typeof batched == 'number' ? batched : 25;

  type reducerState = {
    entries: Item[];
    pageEntries: Item[];
    column: keyof Item;
    direction: 'asc' | 'desc';
    pageSize: number;
    page: number;
    pageCount: number;
  };
  type reducerAction = {
    column?: keyof Item;
    page?: number;
    pageSize?: number;
    entries?: Item[];
    type: 'SORT' | 'PAGE' | 'PAGESIZE' | 'UPDATE';
  };

  const reducerInit = (state: reducerState): reducerState => {
    function sorter(entries: Item[], column: keyof Item) {
      const defaultSort = (a: Item, b: Item) => {
        let a_column: TListEntryValue = a[column] ?? '';
        if (typeof a_column == 'function') {
          if (a_column.length > 0) {
            throw Error(
              `Function ${a_column} takes arguments, only methods without will work.`,
            );
          }
          a_column = a_column();
        }
        let b_column: TListEntryValue = b[column] ?? '';
        if (typeof b_column == 'function') {
          if (b_column.length > 0) {
            throw Error(
              `Function ${b_column} takes arguments, only methods without will work.`,
            );
          }
          b_column = b_column();
        }

        switch (true) {
          case a_column > b_column:
            return 1;

          case a_column === b_column:
            if (a.id && b.id) {
              return a.id > b.id ? 1 : -1;
            }
            return 0;

          case a_column < b_column:
            return -1;

          default:
            return 0;
        }
      };
      entries.sort(itemColumns[column]?.sortCmp ?? defaultSort);

      return entries;
    }

    const sortedEntries = sorter(state.entries.slice(), state.column);

    if (state.direction === 'desc') {
      sortedEntries.reverse();
    }

    const pages = _.chunk(sortedEntries, state.pageSize);
    if (state.page >= pages.length) {
      state.page = pages.length;
    } else if (state.page < 1) {
      state.page = 1;
    }
    state.pageEntries = pages ? pages[state.page - 1] : [];
    state.pageCount = pages.length;
    return state;
  };
  const [
    {
      pageEntries,
      column: sortColumn,
      pageSize,
      page: activePage,
      pageCount,
      direction: sortDir,
    },
    dispatch,
  ] = useReducer(
    (state: reducerState, action: reducerAction): reducerState => {
      switch (action.type) {
        case 'SORT':
          let direction: TSortDir;
          const column: keyof Item = action.column ?? state.column;

          if (state.column === action.column) {
            direction = state.direction === 'asc' ? 'desc' : 'asc';
          } else {
            direction = 'desc';
          }

          if (onSort) onSort(column, direction);

          return reducerInit({
            ...state,
            column: column,
            direction: direction,
          });

        case 'UPDATE':
          return reducerInit({
            ...state,
            entries: action.entries?.slice() ?? state.entries,
          });

        case 'PAGE':
          return reducerInit({
            ...state,
            page: action.page ?? state.page,
          });

        case 'PAGESIZE':
          return reducerInit({
            ...state,
            pageSize: action.pageSize ?? state.pageSize,
          });
      }
      return state;
    },
    reducerInit({
      column:
        parentSortColumn ?? defaultSortColumn ?? Object.keys(itemColumns)[0],
      direction: parentSortDir ?? 'asc',
      entries: items.slice(),
      pageEntries: [],
      pageSize: pagination
        ? sizeOptions[0].value
        : batched
          ? batchSize
          : items.length,
      page: 1,
      pageCount: 1,
    }),
  );

  const onClick = (entry: Item) => () => {
    if (typeof onEntryClick === 'function') {
      onEntryClick(entry);
    }
  };

  useEffect(() => {
    if ([SIZE_LG, SIZE_XL, SIZE_XXL].includes(size)) {
      setPaginationSiblings(3);
    } else {
      setPaginationSiblings(0);
    }
  }, [size]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE',
      entries: items.slice(),
    });
  }, [items]);

  useTransition(
    (
      prevItems,
      prevBatched,
      prevBatchSize,
      prevPagination,
      prevSizeOptions,
    ) => {
      if (
        pagination &&
        (prevPagination === false || !_.isEqual(prevSizeOptions, sizeOptions))
      ) {
        dispatch({
          type: 'PAGESIZE',
          pageSize: sizeOptions[0].value,
        });
      } else if (
        batched &&
        (prevBatched === false || prevBatchSize !== batchSize)
      ) {
        dispatch({
          type: 'PAGESIZE',
          pageSize: batchSize,
        });
      } else if (!pagination && !batched) {
        dispatch({
          type: 'PAGESIZE',
          pageSize: items.length,
        });
      }
    },
    [items, batched, batchSize, pagination, sizeOptions],
  );

  useEffect(() => {
    setSizeOptions(
      (parentSizeOptions ?? [10, 20, 50, 100]).map((size) => {
        return {
          key: size,
          text: size,
          value: size,
        };
      }),
    );
  }, [parentSizeOptions]);

  // Footer Handling
  let footerContent: JSX.Element | null = null;

  if (pagination) {
    footerContent = (
      <div className="list-pagination">
        <Pagination
          activePage={activePage}
          siblingRange={paginationSiblings}
          totalPages={pageCount}
          onPageChange={(event, page) => {
            dispatch({ type: 'PAGE', page: page.activePage as number });
          }}
        />
        <Dropdown
          selection
          value={pageSize}
          options={sizeOptions}
          className="size-options"
          onChange={(event, data) => {
            if (data.value)
              dispatch({ type: 'PAGESIZE', pageSize: +data.value });
          }}
        />
      </div>
    );
  } else if (batched && pageSize < items.length) {
    footerContent = (
      <div className="batching">
        {intl.formatMessage(messages.trans_show_more_entries)} (
        <Button
          className={'link'}
          name={`expand-${batchSize}`}
          onClick={() =>
            dispatch({ type: 'PAGESIZE', pageSize: pageSize + batchSize })
          }
        >
          +{batchSize}
        </Button>
        /
        <Button
          className={'link'}
          name="expand-all"
          onClick={() => dispatch({ type: 'PAGESIZE', pageSize: items.length })}
        >
          {intl.formatMessage(messages.trans_show_all)}
        </Button>
        )
      </div>
    );
  }

  const extraAttributes: Record<string, string> = {};
  if (collapsed) {
    extraAttributes['data-collapsed'] = 'true';
  }

  return (
    <Table
      {...extra_props}
      {...extraAttributes}
      className={classNames(extra_props.className, 'baselist-component', {
        'is-empty': !items || items.length === 0,
      })}
    >
      <Table.Header>
        <Table.Row>
          {_.entries(itemColumns).map(
            ([column, header]: [keyof Item, TableColumn<Item>]) => {
              const labelRender: JSX.Element | string | null =
                typeof header.label === 'function'
                  ? header.label()
                  : header.label;

              const sortable = !!header.sortable;
              const clickHandler = sortable
                ? () => dispatch({ type: 'SORT', column })
                : undefined;
              return (
                <Table.HeaderCell
                  key={column as string}
                  data-column={column}
                  className={classNames(header.className, {
                    sortable: sortable,
                    sorted: column === sortColumn,
                    desc: column === sortColumn && sortDir === 'desc',
                    asc: column === sortColumn && sortDir === 'asc',
                  })}
                  onClick={clickHandler}
                >
                  {header.preLabel
                    ? typeof header.preLabel === 'function'
                      ? header.preLabel()
                      : header.preLabel
                    : null}
                  <span className="wrapper">
                    <span className="column-title">{labelRender}</span>{' '}
                    {sortable && <Icon size="0.8em" name={sortSVG} />}
                  </span>
                  {header.postLabel
                    ? typeof header.postLabel === 'function'
                      ? header.postLabel()
                      : header.postLabel
                    : null}
                </Table.HeaderCell>
              );
            },
          )}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {pageEntries
          ? pageEntries.map((entry, index) => {
              return (
                <Table.Row
                  key={index}
                  data-entry={index}
                  onClick={onClick(entry)}
                >
                  {_.entries(itemColumns).map((headerEntry, index) => {
                    const [column, header] = headerEntry;
                    const value = entry[column];
                    return (
                      <Table.Cell key={index + column}>
                        {header.cellComponent
                          ? header.cellComponent(entry)
                          : value instanceof Function
                            ? value()
                            : value}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              );
            })
          : null}
        {!items || items.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={Object.keys(itemColumns).length}>
              <div
                aria-label="empty-message"
                className="empty-message text-muted text-center text-sm"
              >
                {emptyMessage ??
                  intl.formatMessage(messages.trans_list_empty_message)}
              </div>
            </Table.Cell>
          </Table.Row>
        ) : null}
      </Table.Body>
      {footerContent && (
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={Object.keys(itemColumns).length}>
              {footerContent}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      )}
    </Table>
  );
};

function sortColumnValidator(
  props: Record<string, unknown>,
  propName: string,
  componentName: string,
  location: string,
  propFullName: string,
): Error | null {
  const value = props[propName];

  if (value === undefined) {
    return null;
  }

  if (typeof value !== 'string') {
    return new Error(
      `Invalid prop '${propFullName}' supplied to ${componentName}. must be undefined or a string.`,
    );
  }

  if (!props.items || !(props.items instanceof Array)) {
    return new Error(`'${componentName}' field 'items' must be an Array.`);
  }

  if (!props.items.length) {
    return null;
  }

  props.items.forEach((item) => {
    if (!(value in item))
      throw new Error(
        `Invalid prop '${propFullName}' supplied to ${componentName}. only accepts keys inside items. missing key '${value}'.`,
      );
  });
  return null;
}

BaseList.propTypes = {
  itemColumns: <Item extends TListEntry>(
    props: ListProps<Item>,
    propName: 'itemColumns',
    componentName: string,
  ) => {
    const columns = props.itemColumns;
    if (typeof columns == 'undefined') {
      return new Error(`'${componentName}' field '${propName}' is required.`);
    }
    if (typeof columns != 'object') {
      return new Error(
        `'${componentName}' field '${propName}' needs to be an Record<string, TableColumn>.`,
      );
    }
    if (!props.items.length) {
      return null;
    }
    for (const key in columns) {
      for (const [index, item] of props.items.entries()) {
        if (!(key in item)) {
          return new Error(
            `'${componentName}' field '${propName}' only accepts keys inside items. missing key '${key}' in item of index ${index}`,
          );
        }
      }
    }
    return null;
  },
  items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  sortColumn: sortColumnValidator,
  defaultSortColumn: sortColumnValidator,
  sortDir: PropTypes.oneOf(['asc', 'desc']),
  onEntryClick: PropTypes.func,
  onSort: PropTypes.func,
  emptyMessage: PropTypes.string,
  collapsed: PropTypes.bool,
  pagination: PropTypes.bool,
  batched: <Item extends TListEntry>(
    props: ListProps<Item>,
    propName: keyof ListProps<Item>,
    componentName: string,
    location: string,
  ) => {
    if (props.batched && props.pagination) {
      return new Error(
        `Invalid configuration, ${propName} and pagination supplied to ${componentName}. Only one of them is valid`,
      );
    }

    PropTypes.checkPropTypes(
      { batched: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]) },
      props,
      location,
      componentName,
    );

    return null;
  },
  sizeOptions: PropTypes.arrayOf(PropTypes.number.isRequired),
};
BaseList.defaultProps = {
  collapsed: false,
  sortDir: 'asc',
};

export default BaseList;
